import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

export const ALL_SITES_STORAGE_KEY = 'rita.consignments.allSites';

// sessionStorage throws in private/blocked-storage modes — the viewpoint must
// keep working without persistence.
const readStoredAllSites = (): string | null => {
  try {
    return sessionStorage.getItem(ALL_SITES_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStoredAllSites = (value: string) => {
  try {
    sessionStorage.setItem(ALL_SITES_STORAGE_KEY, value);
  } catch {
    // persistence is best-effort
  }
};

interface UseAllSitesViewpointParams {
  // Committed 'true' / 'false' from the URL state
  allSites: string;
  onChange: (value: string) => void;
}

/**
 * Session-scoped persistence for the consignments viewpoint (`allSites`) — the
 * only filter that survives a reload. The URL stays the source of truth:
 * storage is read once on mount and only when the URL carries no `allSites`
 * param, so a shared link always wins. `useUrlState` strips params equal to
 * their default, which is why param *presence* is read off `useSearchParams`
 * rather than inferred from the value.
 */
export const useAllSitesViewpoint = ({ allSites, onChange }: UseAllSitesViewpointParams) => {
  const [searchParams] = useSearchParams();
  const hasUrlValue = searchParams.has('allSites');
  const isHydratedRef = useRef(false);

  useEffect(() => {
    if (isHydratedRef.current) return;
    isHydratedRef.current = true;

    if (hasUrlValue) return;

    const stored = readStoredAllSites();
    if (stored && stored !== allSites) {
      onChange(stored);
    }
  }, [allSites, hasUrlValue, onChange]);

  // The toggle is the only intentional writer: filter reset and chip removal
  // deliberately leave the viewpoint alone, so no mirroring effect is needed.
  return useCallback(
    (value: string) => {
      writeStoredAllSites(value);
      onChange(value);
    },
    [onChange]
  );
};
